import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate } from "@tanstack/react-router";

import { logError } from "shared/services/ErrorReporting";

import { Button } from "../../components/ui/button";
import { useAuth } from "../../contexts/AuthContext";
import { getLastBookForUser, saveBook } from "../../services/bookService";
import { DEFAULT_BOOK_INPUTS } from "../../shared/constants/book";
import { getErrorMessage } from "../../utils/errorMessages";
import { FirestoreDocumentError } from "../../services/documentParsing";

type LoadError = { message: string; libraryUnreadable: boolean };

const HomePage = () => {
  const {
    state: { authUser },
  } = useAuth();
  const [lastBookId, setLastBookId] = useState<string | null>(null);
  const [createdBookId, setCreatedBookId] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<LoadError | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  // A ref, not the isCreating state: StrictMode runs the effect twice before a
  // state update lands, so a state latch lets both runs create a book.
  const creationRef = useRef<Promise<string> | null>(null);

  const fetchLastBook = useCallback(async () => {
    if (!authUser?.uid) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setLoadError(null);
    try {
      const bookData = await getLastBookForUser(authUser.uid);

      if (bookData && bookData.id) {
        setLastBookId(bookData.id);
      }
    } catch (error) {
      logError(new Error("Failed to fetch last book", { cause: error }));
      const libraryUnreadable = error instanceof FirestoreDocumentError;
      setLoadError({
        message: getErrorMessage(
          libraryUnreadable ? "INVALID_DOCUMENT_ERROR" : undefined,
        ),
        libraryUnreadable,
      });
    } finally {
      setIsLoading(false);
    }
  }, [authUser?.uid]);

  useEffect(() => {
    fetchLastBook();
  }, [fetchLastBook]);

  const createBook = useCallback(async () => {
    if (!authUser?.uid || creationRef.current) return;

    setIsCreating(true);
    setCreateError(null);
    try {
      creationRef.current = saveBook({
        userId: authUser.uid,
        book_inputs: { ...DEFAULT_BOOK_INPUTS },
      });
      setCreatedBookId(await creationRef.current);
    } catch (error) {
      logError(new Error("Failed to create new book", { cause: error }));
      creationRef.current = null;
      setCreateError(getErrorMessage(undefined));
    } finally {
      setIsCreating(false);
    }
  }, [authUser?.uid]);

  // createError also holds the automatic attempt back: without it a re-render
  // would silently retry a write that has already been rejected once.
  useEffect(() => {
    if (isLoading || loadError || lastBookId || createError) return;
    createBook();
  }, [isLoading, loadError, lastBookId, createError, createBook]);

  if (isLoading || isCreating) {
    return <main id="main-content">Loading...</main>;
  }

  // Ahead of the error branch: creating a book from that branch is how the user
  // gets out of an unreadable library, so the redirect has to win once it lands.
  if (createdBookId) {
    return (
      <Navigate to="/book/$bookId/setup" params={{ bookId: createdBookId }} />
    );
  }

  if (loadError || createError) {
    const recoverByCreating = !loadError || loadError.libraryUnreadable;

    return (
      <main id="main-content">
        {loadError && <div role="alert">{loadError.message}</div>}
        {createError && <div role="alert">{createError}</div>}
        <Button
          type="button"
          onClick={() => {
            if (recoverByCreating) {
              createBook();
            } else {
              fetchLastBook();
            }
          }}
        >
          {recoverByCreating ? "Create a new book" : "Try again"}
        </Button>
      </main>
    );
  }

  if (lastBookId) {
    return (
      <Navigate to="/book/$bookId/editor" params={{ bookId: lastBookId }} />
    );
  }

  return <main id="main-content">Loading...</main>;
};

export default HomePage;
